import { getApiEndpoint } from "../config/apiEndpoints";
import { fetchWithAuth } from "../utils/tokenRefresh";

export interface Challenge {
  challenge_id: string;
  order: number;
  title: string;
  prompt: string;
  function_name: string;
  starter_code: string;
  focus_letters: string;
  legend: Record<string, string>;
  season: number;
}

export interface Pitcher {
  pitcher_id: string;
  name: string;
  pitches: string;
}

export interface PitchData {
  pitcher_count: number;
  pitchers: Pitcher[];
}

export interface GradeResult {
  submission_id: string;
  claimed_count: number;
  expected_count: number;
  correct: boolean;
  created_at_iso: string;
}

export interface Submission {
  uuid: string;
  challenge_id: string;
  code: string;
  claimed_count: number;
  expected_count: number;
  correct: boolean;
  created_at: number;
  created_at_iso: string;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(getApiEndpoint(path), { method: "GET" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GET ${path} failed (${res.status}): ${text}`);
  }
  return (await res.json()) as T;
}

export const listChallenges = async (): Promise<Challenge[]> => {
  const data = await getJson<{ challenges: Challenge[] }>("/challenges");
  return data.challenges;
};

export const getChallenge = async (id: string): Promise<Challenge> =>
  getJson<Challenge>(`/challenges/${id}`);

export const getPitchData = async (): Promise<PitchData> =>
  getJson<PitchData>("/pitch-data");

export const submitSolution = async (
  challengeId: string,
  code: string,
  claimedCount: number
): Promise<GradeResult> => {
  const res = await fetchWithAuth(getApiEndpoint("/submissions"), {
    method: "POST",
    body: JSON.stringify({
      challenge_id: challengeId,
      code,
      claimed_count: claimedCount,
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || `Submission failed (${res.status})`);
  }
  return data as GradeResult;
};

export const listMySubmissions = async (
  challengeId?: string
): Promise<Submission[]> => {
  const path = challengeId
    ? `/submissions?challenge_id=${encodeURIComponent(challengeId)}`
    : "/submissions";
  const data = await getJson<{ submissions: Submission[] }>(path);
  return data.submissions;
};
